import { api } from "api";
import { useCallback } from "react";
import { useFeatureFlag } from "utils/useFeatureFlag";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { BulkRequestQueryResult } from "api/responses";
import type { StrictlyBasisPoints } from "@vyne-shared/utils/currency";
import type { UseQueryOptions } from "@tanstack/react-query";

/** The processed bulk request data, for a particular patient. */
export interface BulkRequestRecord extends BulkRequestQueryResult {
    /** The amount we are requesting for payment. */
    amount: StrictlyBasisPoints;

    /** The full name of the Patient/Responsible Party. */
    fullName: string;
}

/** The processed bulk request data, for all patients in the batch. */
export interface BulkRequestQuery {
    /** True if the system is busy with another bulk request for this same provider. */
    busy: boolean;

    /** Processed bulk request data for each patient. */
    rows: readonly BulkRequestRecord[];
}

type Key = typeof BulkRequestQueryKey;
export const BulkRequestQueryKey = Object.freeze(["bulkRequestQuery"] as const);

type Options = Omit<UseQueryOptions<BulkRequestQuery, Error, BulkRequestQuery, Key>, "queryKey">;

const noRows = Object.freeze(Array.of<BulkRequestRecord>());
const busyQuery = Object.freeze({ busy: true, rows: noRows });

/**
 * Client hook to query for Patients/Responsible Parties that are eligible for bulk import.
 * @param options Options to configure query behavior.
 * @returns React Query properties.
 */
export function useBulkRequestQuery(options?: Options) {
    const paymentPlanSwitch = !!useFeatureFlag("payment-plan-switch", true);

    return useQuery({
        ...options,
        queryKey: BulkRequestQueryKey,
        queryFn: async (context) => {
            const response = await api.provider.bulkRequestEligible(context.signal);
            if (response.busy) {
                return busyQuery;
            }

            const newRows = response.results.map<BulkRequestRecord>((x) => mapBulkRequestRecord(x, paymentPlanSwitch));
            return {
                busy: false,
                rows: newRows,
            } as const;
        },
    });
}

/** Return a function, that when called, populates the react-query cache with a synthesized bulk query result
 * indicating that the system is busy performing a bulk operation. */
export function useBulkRequestForceBusy() {
    const queryClient = useQueryClient();

    return useCallback(() => {
        queryClient.setQueryData(BulkRequestQueryKey, busyQuery);
    }, [queryClient]);
}

export function mapBulkRequestRecord(row: BulkRequestQueryResult, paymentPlanSwitch?: boolean): BulkRequestRecord {
    return Object.freeze({
        ...row,
        pmsBalances: Object.freeze(row.pmsBalances),
        amount: row.pmsBalances.balanceEstimated,
        fullName: `${row.givenName} ${row.familyName}`,
        // when payment-plan-switch feature is off, disregard information about enrollment in active payment plans
        hasActivePaymentPlan: (paymentPlanSwitch ?? true) ? ensureBool(row.hasActivePaymentPlan) : false,
    });
}

function ensureBool(value: boolean | null | undefined): boolean {
    return !!value;
}
